// app/javascript/components/RankManagement.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { t } from '../utils/i18n';
import Pagination from './Pagination';

const RankManagement = ({ onBack, api }) => {
  const [ranks, setRanks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    somali_name: ''
  });

  useEffect(() => {
    fetchRanks();
  }, []);

  const fetchRanks = async () => {
    try {
      setLoading(true);
      const data = await api.get('/ranks');
      if (Array.isArray(data)) {
        setRanks(data);
      } else if (data && Array.isArray(data.ranks)) {
        setRanks(data.ranks);
      } else {
        setRanks([]);
      }
    } catch (error) {
      console.error('Failed to fetch ranks:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedRanks = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    let filtered = ranks.filter(rank =>
      rank.name?.toLowerCase().includes(term) ||
      rank.somali_name?.toLowerCase().includes(term)
    );

    const [field, order] = sortBy.split('-');
    return filtered.sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      if (field === 'id') {
        aValue = parseInt(aValue || 0, 10);
        bValue = parseInt(bValue || 0, 10);
      } else {
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [ranks, searchTerm, sortBy]);

  const totalPages = Math.ceil(filteredAndSortedRanks.length / itemsPerPage) || 1;
  const paginatedRanks = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedRanks.slice(start, start + itemsPerPage);
  }, [filteredAndSortedRanks, currentPage, itemsPerPage]);

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setDropdownOpen(false);
  };

  const handleAdd = () => {
    setFormData({ name: '', somali_name: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (rank) => {
    setFormData({
      name: rank.name || '',
      somali_name: rank.somali_name || ''
    });
    setEditingId(rank.id);
    setShowForm(true);
  };

  const handleDelete = async (id, rankName) => {
    if (window.confirm(`Are you sure you want to delete rank "${rankName}"?`)) {
      setLoading(true);
      try {
        const { response, data } = await api.delete(`/ranks/${id}`);
        if (response.ok) {
          setRanks(prev => prev.filter(r => r.id !== id));
        } else {
          alert((data && data.message) || 'Failed to delete rank');
        }
      } catch (error) {
        console.error('Failed to delete rank:', error);
        alert('Failed to delete rank');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter rank name (English)');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        rank: {
          name: formData.name.trim(),
          somali_name: formData.somali_name.trim()
        }
      };

      if (editingId) {
        const { response, data } = await api.put(`/ranks/${editingId}`, payload);
        if (response.ok) {
          setShowForm(false);
          setFormData({ name: '', somali_name: '' });
          fetchRanks();
        } else {
          const errMsg = (data && (data.message || (data.errors && data.errors.join(', ')))) || 'Failed to update rank';
          alert(errMsg);
        }
      } else {
        const { response, data } = await api.post('/ranks', payload);
        if (response.ok) {
          setShowForm(false);
          setFormData({ name: '', somali_name: '' });
          fetchRanks();
        } else {
          const errMsg = (data && (data.message || (data.errors && data.errors.join(', ')))) || 'Failed to create rank';
          alert(errMsg);
        }
      }
    } catch (error) {
      console.error('Failed to save rank:', error);
      alert('Failed to save rank: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ name: '', somali_name: '' });
    setEditingId(null);
  };

  return (
    <>
      {/* Add / Edit Form */}
      {showForm && (
        <div className="mb_40 baseTableWrap">
          <div className="dFlex baseTableHd mb_16">
            <h2 className="txt20 fwMedium colorBlack">
              {editingId ? 'Edit Rank' : 'Add New Rank'}
            </h2>
          </div>
          <form onSubmit={handleSubmit} style={{ padding: '0px 20px 20px 20px' }}>
            <div className="row">
              <div className="col-md-6 mb_20">
                <label className="mb_8" style={{ fontWeight: 500 }}>
                  {t('rankName', 'Rank Name (English)')} <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. S/Guud: or General"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8" style={{ fontWeight: 500 }}>
                  {t('somaliName', 'Rank Name (Somali)')}
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Sareeye Guud (General)"
                  value={formData.somali_name}
                  onChange={(e) => setFormData({ ...formData, somali_name: e.target.value })}
                />
              </div>
            </div>
            <div className="dFlex mt_18 btnSet">
              <button type="submit" className="btnBig me-3" disabled={loading}>
                {loading ? 'Saving...' : (editingId ? t('update', 'Update') : t('add', 'Add Rank'))}
              </button>
              <button type="button" className="btnBig btnSecondary" onClick={handleCancel} disabled={loading}>
                {t('cancel', 'Cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Rank List Table */}
      <div className="mb_40 baseTableWrap">
        <div className="dFlex baseTableHd mb_16 align-items-center justify-content-between">
          <h2 className="txt20 fwMedium colorBlack">
            {t('rankList', 'Rank List')} ({filteredAndSortedRanks.length})
          </h2>
          <div className="dFlex baseTableTR align-items-center">
            <button className="btnAdd me-3" onClick={handleAdd}>
              {t('addRank', 'Add Rank')}
            </button>
            <div className="searchBox">
              <input
                type="text"
                placeholder={t('searchRanks', 'Search ranks...')}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <button type="button">
                <img src="/images/search1.png" alt="" />
              </button>
            </div>
            <div className="dropdown dropdownSortBy ms-2">
              <button
                className="dropdown-toggle"
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {t('sort_by', 'Sort by')}
              </button>
              <ul className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}>
                <li>
                  <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSortSelect('name-asc'); }}>
                    {t('nameAsc', 'Name A-Z')}
                  </a>
                </li>
                <li>
                  <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSortSelect('name-desc'); }}>
                    {t('nameDesc', 'Name Z-A')}
                  </a>
                </li>
                <li>
                  <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSortSelect('id-asc'); }}>
                    {t('oldestFirst', 'Oldest First')}
                  </a>
                </li>
                <li>
                  <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSortSelect('id-desc'); }}>
                    {t('newestFirst', 'Newest First')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="baseTableContainer">
          <table className="baseTable">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>#</th>
                <th>{t('rankName', 'Rank Name (English)')}</th>
                <th>{t('somaliName', 'Rank Name (Somali)')}</th>
                <th>{t('created_date', 'Created Date')}</th>
                <th style={{ minWidth: '175px' }}>{t('actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && paginatedRanks.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }} className="colorGray">
                    {t('loading', 'Loading ranks...')}
                  </td>
                </tr>
              ) : paginatedRanks.length > 0 ? (
                paginatedRanks.map((rank, index) => (
                  <tr key={rank.id}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td style={{ fontWeight: 600, color: '#111' }}>{rank.name}</td>
                    <td>{rank.somali_name || '—'}</td>
                    <td>{rank.created_at ? new Date(rank.created_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <div className="dFlex optBlock">
                        <button className="optBtn" onClick={() => handleEdit(rank)} disabled={loading}>
                          <img src="/images/edit.png" alt="" />
                          <span>{t('edit', 'Edit')}</span>
                        </button>
                        <span className="bar">|</span>
                        <button className="optBtn" onClick={() => handleDelete(rank.id, rank.name)} disabled={loading}>
                          <img src="/images/delete.png" alt="" />
                          <span>{t('delete', 'Delete')}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                    {searchTerm ? `No ranks found matching "${searchTerm}"` : 'No ranks found. Click "Add Rank" to create one.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt_20">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default RankManagement;
